import type { Edge, Node } from '@vue-flow/core'
import type {
  AutomationFlowStatus,
  AutomationFlowStepInterface,
  AutomationFlowStepPayload,
  AutomationStepType,
  AutomationWaitUntilEventHappensEventType,
  AutomationWaitUntilEventHappensPayload,
  AutomationWaitUntilEventHappensLegacyMessageEventType,
} from '@/data/models/domain/automations/types'
import type { AutomationStepRootCategory } from '@/composition/domain/automations/types'
import type BaseFormService from '@/services/forms/baseFormService'
import type { FormBuilderInterface } from '@/services/forms/baseForm/types'
import type { IconName } from '@/assets/icons/icons'
import type { RegisteredServices } from '@/core/container/types'
import type { Optional, PromiseOr } from '@/types'
import type { DependencyToPreload, FormattedTextNode } from '@/services/types'
import { isRecordUnknown } from '@/utils/typeGuards'
import {
  ShopifyDynamicField,
  WooCommerceCouponDynamicField,
  WooCommerceDynamicField,
  type ShopifyVariable,
  type WooCommerceVariable,
} from '@/services/domain/externalIntegrations/types'
import { mapStringOrNumberToEnum } from '@/utils/mapStringOrNumberToEnum'
import { TmInvalidEnumValueError } from '@/core/error/tmInvalidEnumValueError'
import type { AutomationFlowValidationResultInterface } from '@/data/models/domain/automations/typesOfAutomationFlowValidation'
import type { StyleColors } from '@/constants/styleColors'

export enum AutomationNodeType {
  // Used to trigger repositioning of the nodes when we update a step data
  NODE_CHANGE_TRIGGER = 'nodeChangeTrigger',
  // Used to display flow settings
  SETTINGS = 'settings',
  STEP = 'step',
  STARTER = 'starter',
  PLACE_TO_ADD = 'placeToAdd',
  // Used to display delay for "wait until event happens" step
  EXTRA_INFO = 'extraInfo',
  // Used to display the button to add a new path in reply based split step
  RULE_REPLY_BASED_NEW_PATH_BUTTON = 'ruleReplyBasedNewPathButton',
}

export type AutomationStepNodeEdgeOffset = 'none' | 'small' | 'medium'

interface AbstractAutomationNode<
  Type extends AutomationNodeType,
  Data extends Record<string, unknown> = {},
> extends Node {
  type: Type
  data: Data & {
    edgeOffset?: AutomationStepNodeEdgeOffset
    minWidth?: number
  }
}

export type AutomationNodeChangeTriggerNode = AbstractAutomationNode<AutomationNodeType.NODE_CHANGE_TRIGGER>

export type AutomationSettingsNode = AbstractAutomationNode<AutomationNodeType.SETTINGS>

export type AutomationStarterNode = AbstractAutomationNode<AutomationNodeType.STARTER>

export type AutomationPlaceToAddNode = AbstractAutomationNode<
  AutomationNodeType.PLACE_TO_ADD,
  {
    isNextStep: boolean
    availableStepCategories: AutomationStepRootCategory[]
    parentStepLocalId: number
  }
>

export type AutomationExtraInfoNode = AbstractAutomationNode<
  AutomationNodeType.EXTRA_INFO,
  {
    parentStepLocalId: number
    relatedStepLocalId: number
    relatedStepType: AutomationStepType
    content: string
  }
>

export type AutomationRuleReplyBasedNewPathButtonNode = AbstractAutomationNode<
  AutomationNodeType.RULE_REPLY_BASED_NEW_PATH_BUTTON,
  {
    replyBasedSplitStepLocalId: number
  }
>

export type AutomationStepNode<Type extends AutomationStepType = AutomationStepType> = AbstractAutomationNode<
  AutomationNodeType.STEP,
  {
    type: Type
    localId: number
  }
>

export type AutomationNode =
  | AutomationNodeChangeTriggerNode
  | AutomationSettingsNode
  | AutomationStarterNode
  | AutomationPlaceToAddNode
  | AutomationExtraInfoNode
  | AutomationStepNode<AutomationStepType>
  | AutomationRuleReplyBasedNewPathButtonNode

export type AutomationNodeData = AutomationNode['data']

export type AutomationEdgeData = {
  width?: number
  height?: number
  skipLayout?: boolean
}

export type AutomationGoToEdgeData = AutomationEdgeData & {
  goToStepLocalId: number
  targetStepLocalId: number
}

export type AutomationEdge = Edge<AutomationEdgeData | AutomationGoToEdgeData>

export enum AutomationEdgeTargetHandle {
  TOP = 'top',
  BOTTOM = 'bottom',
  GO_TO = 'goTo',
}

export type AutomationGraph = {
  nodes: AutomationNode[]
  edges: AutomationEdge[]
}

export interface AutomationFlowStepAddStepParams<Type extends AutomationStepType = AutomationStepType> {
  automationId: string
  parentStepLocalId?: number
  payload?: Partial<AutomationFlowStepPayload<Type>>
  title?: string
}

export interface AutomationFlowStepEditingAbilityParams {
  flowStatus: AutomationFlowStatus
  hasContactsInFlow: boolean
}

export type AutomationFlowStepEditingAbility =
  | {
      canEditStepPayload: false
      reason: 'flowWithContacts' | 'stoppedFlow' | 'completedFlow'
    }
  | {
      canEditStepPayload: true
      requiredAction?: 'moveFlowToDraft'
    }

export type AutomationFlowValueComparator<Value> = (value1: Value, value2: Value) => boolean

export type AutomationFlowObjectsComparatorsMap<Object extends Record<string, unknown>> = {
  [Key in keyof Required<Object>]:
    | 'default' // use default comparator
    | 'skip' // skip comparison
    | AutomationFlowValueComparator<Object[Key]>
}

export type AutomationFlowStepPayloadValueComparatorsMap<Type extends AutomationStepType> =
  AutomationFlowObjectsComparatorsMap<Omit<AutomationFlowStepPayload<Type>, 'type'>>

export type AutomationWuehEventPayloadValueComparatorsMap<
  EventType extends AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType,
> = AutomationFlowObjectsComparatorsMap<Omit<AutomationWaitUntilEventHappensPayload<EventType>, 'type'>>

export interface AutomationFlowStepServiceInterface<Type extends AutomationStepType, ExtraDataToDelete = unknown> {
  getStepType(): Type
  addStepToFlow(params: AutomationFlowStepAddStepParams<Type>): number // returns localId of the added step
  deleteStepFromFlow(params: { automationId: string; localId: number }, extraDataToDelete?: ExtraDataToDelete): void
  moveStepOnFlow(params: { automationId: string; localId: number; newParentStepLocalId?: number }): void
  getDerivedValidationResults(params: {
    automationId: string
    localId: number
  }): AutomationFlowValidationResultInterface[]
  getEditingAbility(params: AutomationFlowStepEditingAbilityParams): AutomationFlowStepEditingAbility
  isAvailableAsAChildFor(step: AutomationFlowStepInterface<AutomationStepType>): boolean
  replaceToOnParentReplaced(toStep: AutomationStepType): Optional<AutomationStepType>
  getStepNodeDependencies(step: AutomationFlowStepInterface<Type>): DependencyToPreload[]
  getPayloadValueComparators(): AutomationFlowStepPayloadValueComparatorsMap<Type>
  isStepsEqual(step1: AutomationFlowStepInterface<Type>, step2: AutomationFlowStepInterface<Type>): boolean
}

export interface AutomationFlowStepFormServiceInterface<
  Type extends AutomationStepType,
> extends BaseFormService<FormBuilderInterface> {
  getStepType(): Type
  setEditingStepId(automationId: string, stepLocalId: number): void
  populateFromStep(automationId: string, stepLocalId: number): PromiseOr<void>
  isPayloadChanged(): boolean
  setIsFormBuildedAndPopulated(): void
  isFormBuildedAndPopulated(): boolean
}

export interface AutomationFlowStepWithAdditionalFiltersFormServiceInterface {
  getFilterFieldName(): string
}

export interface StepNodeProccessParams<Type extends AutomationStepType = AutomationStepType> {
  graph: AutomationGraph
  step: AutomationFlowStepInterface<Type>
  isEditing?: boolean
}

export interface AutomationStepNodeBuilderServiceInterface<Type extends AutomationStepType = AutomationStepType> {
  processStepNode(params: StepNodeProccessParams<Type>): void
}

export const NEW_AUTOMATION_ID = 'newAutomationFlow'

export const AUTOMATION_WEBHOOK_STEPS_WARNING_THRESHOLD = 5
export const AUTOMATION_WEBHOOK_STEPS_LIMIT = 10

export type AutomationStepColor = 'red' | 'green' | 'purple' | 'blue'

export interface AutomationStepConfig {
  rootCategory: AutomationStepRootCategory
  name: string
  color?: AutomationStepColor
  iconName: IconName
  additionalIcon?: IconName
  additionalIconColor?: StyleColors
  hasWaitingStatistics?: boolean
  isComingSoon?: boolean
  isNew?: boolean
}

export type AutomationStepConfigMap = Record<AutomationStepType, AutomationStepConfig>

export type AutomationStepMenuStep = AutomationStepType

export const isAutomationStepMenuStep = (
  item: AutomationStepMenuStep | AutomationStepMenuSubCategory,
): item is AutomationStepMenuStep => {
  return typeof item === 'string'
}

export type AutomationFlowCanActivateResult = { canActivate: true } | { canActivate: false; tooltip: string }

export interface AutomationStepMenuSubCategory {
  id: string
  name: string
  icon?: IconName
  items: AutomationStepMenuStep[]
  isOpenByDefault?: boolean
}

export const isAutomationStepMenuSubCategory = (
  item: AutomationStepMenuStep | AutomationStepMenuSubCategory,
): item is AutomationStepMenuSubCategory => {
  return isRecordUnknown(item) && 'items' in item && Array.isArray(item.items)
}

type AutomationStepMenuRootCategory = Extract<
  AutomationStepRootCategory,
  AutomationStepRootCategory.TRIGGER | AutomationStepRootCategory.ACTION | AutomationStepRootCategory.RULE
>

export type AutomationStepMenu = Record<
  AutomationStepMenuRootCategory,
  Array<AutomationStepMenuSubCategory | AutomationStepMenuStep>
>

export const automationFlowTableId = 'AutomationFlowTable' as RegisteredServices
export const automationTemplatesTableId = 'AutomationTemplatesTable' as RegisteredServices

export interface AutomationFlowDetailsResolverParams {
  templateIdRequestParam: string
  automationIdRequestParam: string
  fillFromRequestParam: string
}

export interface AutomationSplitStepDeleteExtraData {
  keepBranchLocalId: number // 0 - delete all paths, other - keep path with this local id
}

export interface AutomationAdditionalFilterPreview {
  title?: string
  formattedTextNodes: FormattedTextNode[]
}

export type AutomationTriggerNewInboundSmsReceivedFilterName = 'phones' | 'contentFilter' | 'country'

export type AutomationTriggerContactCreatedFilterName = 'source' | 'createdBy'

export const automationTriggerContactCreatedDedicatedFilterNames: AutomationTriggerContactCreatedFilterName[] = [
  'source',
  'createdBy',
]

export type AutomationLogFilterName = 'automationId' | 'contactId' | 'step' | 'status' | 'createdAt'

export type AutomationTemplatesFilters = 'category' | 'channel'

export interface AutomationStepPayloadConverterInterface<
  FromType extends AutomationStepType,
  ToType extends AutomationStepType,
> {
  canConvert(fromStepType: AutomationStepType, toStepType: AutomationStepType): boolean
  convert(stepPayload: AutomationFlowStepPayload<FromType>, toStepType: ToType): AutomationFlowStepPayload<ToType>
}

export interface AutomationWuehEventService<
  EventType extends AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType,
> {
  getEventDerivedValidationResults(
    event: AutomationWaitUntilEventHappensPayload<EventType>,
    wuehStepLocalId: number,
  ): AutomationFlowValidationResultInterface[]
  getEventDependencies: (event: AutomationWaitUntilEventHappensPayload<EventType>) => DependencyToPreload[]
  getEventPayloadValueComparators(
    event: AutomationWaitUntilEventHappensPayload<EventType>,
  ): AutomationWuehEventPayloadValueComparatorsMap<EventType>
}

export const tryGetShopifyVariableType = (variable: string): ShopifyDynamicField => {
  const parts = variable.split('.')
  let variableWithoutOrder: string

  if (parts.length === 4) {
    parts.splice(1, 1)
    variableWithoutOrder = parts.join('.')
  } else {
    variableWithoutOrder = variable
  }

  return mapStringOrNumberToEnum(variableWithoutOrder, ShopifyDynamicField)
}

const STORE_METAFIELD_MARKER = '.metafields.'

export const tryGetStoreCustomFieldName = (variable: string): string | null => {
  if (!variable.includes(STORE_METAFIELD_MARKER)) {
    return null
  }

  return variable.slice(variable.indexOf(STORE_METAFIELD_MARKER) + STORE_METAFIELD_MARKER.length)
}

export const isShopifyVariable = (variable: string): variable is ShopifyVariable => {
  if (variable.startsWith('shopify.') && tryGetStoreCustomFieldName(variable) !== null) {
    return true
  }

  try {
    tryGetShopifyVariableType(variable)
    return true
  } catch (e) {
    if (e instanceof TmInvalidEnumValueError) {
      return false
    }
    throw e
  }
}

export const tryGetWooCommerceVariableType = (variable: string): WooCommerceDynamicField => {
  const parts = variable.split('.')
  let variableWithoutOrder: string

  if (parts.length === 4) {
    parts.splice(1, 1)
    variableWithoutOrder = parts.join('.')
  } else {
    variableWithoutOrder = variable
  }

  return mapStringOrNumberToEnum(variableWithoutOrder, WooCommerceDynamicField)
}

export const isWooCommerceVariable = (variable: string): variable is WooCommerceVariable => {
  if (variable.startsWith('woocommerce.') && tryGetStoreCustomFieldName(variable) !== null) {
    return true
  }

  try {
    tryGetWooCommerceVariableType(variable)
    return true
  } catch (e) {
    if (e instanceof TmInvalidEnumValueError) {
      return false
    }
    throw e
  }
}

export const tryGetWooCommerceCouponVariableType = (variable: string): WooCommerceCouponDynamicField => {
  const parts = variable.split('.')
  let variableWithoutOrder: string

  if (parts.length === 3) {
    parts.splice(1, 1)
    variableWithoutOrder = parts.join('.')
  } else {
    variableWithoutOrder = variable
  }

  return mapStringOrNumberToEnum(variableWithoutOrder, WooCommerceCouponDynamicField)
}

export const isWooCommerceCouponVariable = (variable: string): variable is WooCommerceCouponDynamicField => {
  try {
    tryGetWooCommerceCouponVariableType(variable)
    return true
  } catch (e) {
    if (e instanceof TmInvalidEnumValueError) {
      return false
    }
    throw e
  }
}

export const HUBSPOT_VARIABLE_PREFIX = 'hubspot.'

// HubSpot dynamic variables look like `hubspot.<object>.<property>` (e.g. hubspot.contact.first_name).
// Unlike Shopify/WooCommerce the property set is open (custom HubSpot properties), so there is no enum —
// a prefix check is enough to route them to the HubSpot menu mapper.
export const isHubspotVariable = (variable: string): boolean => variable.startsWith(HUBSPOT_VARIABLE_PREFIX)
